import { AfterViewInit, ChangeDetectionStrategy, Component, computed, inject, model, OnDestroy, OnInit, QueryList, signal, ViewChildren } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { NgbCollapseModule } from '@ng-bootstrap/ng-bootstrap';
import { Subscription } from 'rxjs';
import { dateFormat, GenderEnum, PAGE, SearchFields } from '../../core';
import { ExtendedSearchItems } from '../../models';
import { ListService, UserService } from '../../services';
import { BaseComponent, ConvertDatePipe } from '../../shared';
import { NestedPlanTableComponent } from '../nested-plan-table';
import { PaginationComponent } from '../pagination';
import { PatientActionComponent } from '../patient-action';
import { SortableHeaderComponent, SortEvent } from '../sortable-header';


/**
 * Nested patient table component.
 */
@Component({
	selector: 'app-nested-patient-table',
	imports: [
		ConvertDatePipe,
		NgbCollapseModule,
		PaginationComponent,
		PatientActionComponent,
		SortableHeaderComponent,
		NestedPlanTableComponent
	],
	templateUrl: './nested-patient-table.component.html',
	changeDetection: ChangeDetectionStrategy.Eager
})
export class NestedPatientTableComponent extends BaseComponent implements OnInit, AfterViewInit, OnDestroy {

	private readonly router = inject(Router);
	private readonly listSrv = inject(ListService);
	private readonly userSrv = inject(UserService);

	patientList = signal<ExtendedSearchItems[][]>([]);
	collectionSize = signal<number>(0);
	activeColumn = signal<string | null>(null);
	isTlhexUser = toSignal(this.userSrv.isTlhex());

	selectedPlan = model<ExtendedSearchItems | null>(null);
	selectedPatientId = computed((() => this.selectedPlan()?.patientId));

	readonly fields = SearchFields;
	readonly genderClass = genderClass;
	readonly dateFormat = dateFormat();
	@ViewChildren(SortableHeaderComponent) private readonly _headers = new QueryList<SortableHeaderComponent>();
	private _searchResultSub$?: Subscription;


	ngOnInit(): void {
		this.listSrv.resetSearchResult();
		this.listSrv.resetAdvancecSearch();
		this._searchResultSub$ = this.listSrv.getAdvancedSearchResult().subscribe(list => {
			this.collectionSize.set(list?.count ?? 0);
			this.patientList.set(list?.items ?? []);
			if (this.selectedPlan()) this.initSelectedPlan();
		});
	}

	ngAfterViewInit(): void {
		setTimeout(() => this.updateSortHeaders());
	}

	private updateSortHeaders(): void {
		const sortOrder = this.listSrv.getSortOrder();
		if (sortOrder) {
			this._headers.find(header => header.name() == sortOrder.name)?.direction.set(sortOrder.desc ? 'desc' : 'asc');
			this.activeColumn.set(sortOrder.name);
		}
	}

	private initSelectedPlan(): void {
		for (const pat of this.patientList()) {
			for (const p of pat.filter(p => p.patientId === this.selectedPlan()!.patientId)) {
				p.opened = true;
			}
		}
	}

	/**
	* Update column sort order and sort list.
	*/
	onSort({ column, direction }: SortEvent): void {
		// resetting other headers
		for (const header of this._headers.filter(h => h.name() !== column)) {
			header.reset();
		}
		// sorting
		if (direction === '') {
			direction = column === this.listSrv.PATIENT_DEFAULT_SORT.name || !this.listSrv.PATIENT_DEFAULT_SORT.desc ? 'asc' : 'desc';
			column = this.listSrv.PATIENT_DEFAULT_SORT.name;
		}
		this.listSrv.sort(column, direction);
		this.updateSortHeaders();
	}

	/**
	* Navigate to patient creation page.
	*/
	createPatient(): void {
		this.router.navigateByUrl(`/${PAGE.PATIENT_NEW}`, { state: { previousPageList: true } });
	}


	ngOnDestroy(): void {
		this._searchResultSub$?.unsubscribe();
		this.listSrv.resetSearchResult();
		this.selectedPlan.set(null);
	}
}


/** 
 * Get css class according to gender.
*/
export const genderClass = (gender: GenderEnum): string => {
	switch (gender) {
		case GenderEnum.FEMALE: return 'f';
		case GenderEnum.MALE: return 'm';
		case GenderEnum.OTHER: return 'o';
		default: return '';
	}
}
